import type {
  AutostoreGridDto,
  AutostorePortDto,
  WorkstationSummaryDto
} from "~/types/api";

import { possibleGridCoordinates } from "./workstationConstants";

export type GridXY = { x: number; y: number };

/**
 * All (x,y) pairs in {@link possibleGridCoordinates}², ordered with (0,0) first,
 * then by increasing distance from the origin (x²+y²), then x, then y — so new
 * ports/totes “make room” near the workstation origin instead of (-9,-9).
 */
const gridCandidatesNearOriginFirst: GridXY[] = (() => {
  const pairs: GridXY[] = [];
  for (const x of possibleGridCoordinates) {
    for (const y of possibleGridCoordinates) {
      pairs.push({ x, y });
    }
  }
  pairs.sort((a, b) => {
    const da = a.x * a.x + a.y * a.y;
    const db = b.x * b.x + b.y * b.y;
    if (da !== db) {
      return da - db;
    }
    const aNeg = a.x < 0 || a.y < 0 ? 1 : 0;
    const bNeg = b.x < 0 || b.y < 0 ? 1 : 0;
    if (aNeg !== bNeg) {
      return aNeg - bNeg;
    }
    if (a.x !== b.x) {
      return a.x - b.x;
    }
    return a.y - b.y;
  });
  return pairs;
})();

/** First grid cell not used by any port or tote (backend rejects duplicate coordinates). */
export function firstUnusedGridPositionAmongPortsAndTotes(
  portCoordinates: GridXY[],
  toteCoordinates: GridXY[]
): GridXY {
  const used = new Set<string>([
    ...portCoordinates.map((c) => `${c.x},${c.y}`),
    ...toteCoordinates.map((c) => `${c.x},${c.y}`)
  ]);
  for (const { x, y } of gridCandidatesNearOriginFirst) {
    const key = `${x},${y}`;
    if (!used.has(key)) {
      return { x, y };
    }
  }
  return { x: 0, y: 0 };
}

/**
 * Physical port IDs available on a grid for workstation port pickers.
 * Prefer {@link AutostoreGridDto.autostorePorts}; when that is empty (some APIs
 * omit or clear it), derive IDs from workstations on the same grid and finally
 * from the workstation being edited so selects stay in-range with real data.
 */
export function getEffectiveGridPortIds(
  grid: AutostoreGridDto | undefined,
  workstations: WorkstationSummaryDto[] | undefined,
  gridId: string | undefined,
  fallbackWorkstation: WorkstationSummaryDto | undefined
): number[] {
  const ids = new Set<number>();
  for (const p of grid?.autostorePorts ?? []) {
    ids.add(p.portId);
  }

  if (ids.size === 0 && gridId && workstations?.length) {
    for (const ws of workstations) {
      if (ws.autostoreGridId !== gridId) {
        continue;
      }
      for (const port of ws.ports ?? []) {
        ids.add(port.portId);
        if (port.parentPortId != null) {
          ids.add(port.parentPortId);
        }
      }
    }
  }

  if (ids.size === 0 && fallbackWorkstation?.ports?.length) {
    if (!gridId || fallbackWorkstation.autostoreGridId === gridId) {
      for (const port of fallbackWorkstation.ports) {
        ids.add(port.portId);
        if (port.parentPortId != null) {
          ids.add(port.parentPortId);
        }
      }
    }
  }

  return [...ids].sort((a, b) => a - b);
}

/**
 * Parent multiport list from the API plus any {@link parentPortId} already on the form
 * so MUI Select values stay in range when `get-multi-ports` returns an empty list.
 */
export function mergeMultiportsWithFormParentIds(
  apiPorts: AutostorePortDto[] | undefined,
  formPorts: { parentPortId?: number }[] | undefined
): AutostorePortDto[] {
  const list: AutostorePortDto[] = [...(apiPorts ?? [])];
  const seen = new Set(list.map((m) => m.portId));
  for (const row of formPorts ?? []) {
    const pid = row.parentPortId;
    if (pid != null && !seen.has(pid)) {
      seen.add(pid);
      list.push({
        portId: pid,
        categories: [],
        coordinates: [],
        portType: "Multi"
      });
    }
  }
  return list;
}

/**
 * Port IDs the user may pick for `ports[rowIndex].portId` when editing a workstation:
 * grid ports not claimed by another workstation on the same grid (including via
 * parentPortId), not already chosen on another row in this form, always including the
 * current row's value so the select stays valid.
 */
export function getSelectablePortIdsForWorkstationFormRow(
  gridPortIds: number[],
  workstations: WorkstationSummaryDto[] | undefined,
  gridId: string | undefined,
  currentWorkstationId: string | undefined,
  formPortIdsByRow: number[],
  rowIndex: number
): number[] {
  if (!gridPortIds.length) return [];

  if (!gridId || !workstations?.length || !currentWorkstationId) {
    return gridPortIds;
  }

  const currentRowPortId = formPortIdsByRow[rowIndex];

  const usedOnAnotherWorkstation = (portId: number) =>
    workstations.some(
      (ws) =>
        ws.id !== currentWorkstationId &&
        ws.ports.length > 0 &&
        ws.autostoreGridId === gridId &&
        ws.ports.some((p) => p.portId === portId || p.parentPortId === portId)
    );

  const takenByAnotherRow = (portId: number) =>
    formPortIdsByRow.some((id, idx) => idx !== rowIndex && id === portId);

  return gridPortIds.filter(
    (portId) =>
      portId === currentRowPortId ||
      (!usedOnAnotherWorkstation(portId) && !takenByAnotherRow(portId))
  );
}

export function pickDefaultPortIdForNewPortRow(
  gridPortIds: number[],
  workstations: WorkstationSummaryDto[] | undefined,
  gridId: string | undefined,
  currentWorkstationId: string | undefined,
  existingFormPortIds: number[]
): number | null {
  const nextRowIndex = existingFormPortIds.length;
  const selectable = getSelectablePortIdsForWorkstationFormRow(
    gridPortIds,
    workstations,
    gridId,
    currentWorkstationId,
    existingFormPortIds,
    nextRowIndex
  );
  return selectable[0] ?? null;
}

export function isWorkstationInList(
  workstations: WorkstationSummaryDto[] | undefined,
  workstationId: string | undefined | null
): boolean {
  return Boolean(
    workstationId && workstations?.some((w) => w.id === workstationId)
  );
}

export type WorkstationAssignmentState = {
  selectedId: string;
  /**
   * True when the select differs from the site workstation.
   * Empty select while the site WS is hidden by the grid filter is not dirty
   * (not treated as unassign intent).
   */
  isDirty: boolean;
  /** True when the select matches the site workstation and both are set. */
  isAssigned: boolean;
};

/**
 * Shared assign/unassign select state for settings workstation selectors
 */
export function getWorkstationAssignmentState(
  workstations: WorkstationSummaryDto[] | undefined,
  siteWorkstationId: string | undefined | null,
  selectedWorkstationId: string | undefined | null
): WorkstationAssignmentState {
  const siteId = siteWorkstationId ?? "";
  const selectedId = isWorkstationInList(workstations, selectedWorkstationId)
    ? (selectedWorkstationId as string)
    : "";
  const isSiteWorkstationHiddenByGridFilter =
    Boolean(siteId) && !isWorkstationInList(workstations, siteId);
  const isDropdownEmptyWhileSiteWsHiddenByGrid =
    !selectedId && isSiteWorkstationHiddenByGridFilter;

  return {
    selectedId,
    isDirty: selectedId !== siteId && !isDropdownEmptyWhileSiteWsHiddenByGrid,
    isAssigned: Boolean(siteId) && selectedId === siteId
  };
}

/** Sort by grid name then deviceId and when `selectedGridId` is set, keep only that grid. */
export function sortWorkstationsByGrid(
  workstations: WorkstationSummaryDto[],
  selectedGridId: string | undefined
): WorkstationSummaryDto[] {
  const sorted = workstations.toSorted(
    (a, b) =>
      a.autostoreGridName.localeCompare(b.autostoreGridName) ||
      a.deviceId.localeCompare(b.deviceId)
  );
  if (!selectedGridId) {
    return sorted;
  }
  return sorted.filter((w) => w.autostoreGridId === selectedGridId);
}
